// assets/eb-region-interest-form.js
//
// International-waitlist form web component. Used by the out-of-market region
// gate popup (sections/region-gate-modal.liquid) and the standalone page
// (sections/main-region-interest.liquid). POSTs to the Shopify App Proxy at
// /apps/api/international-interest (Element Ops), which records the signup as
// a completed response in the `international-shipping` survey.
//
// Modeled on class BisForm (assets/global.js). Same JS-load-failure defense:
// the submit control is type="button" and the <form> has onsubmit="return
// false;", so a failed upgrade can't fall through to a native page-reloading
// submit.
//
// Country field: the <select> is populated server-side with Shopify's
// all_country_option_tags (values are English country names). We prefill it
// from window.EBGeo (the detected ISO code → country name via Intl.
// DisplayNames) but leave it fully editable — geo is wrong often enough
// (VPNs, travellers, corporate egress) that a locked field would corrupt the
// demand map. The raw detected ISO is sent separately as detected_country_code.
//
// Wrapped in a guard block so the file is safe to load twice (a second classic
// <script> re-declaring a top-level class throws "Identifier 'RegionInterestForm'
// has already been declared"); the class is block-scoped inside it.
if (!customElements.get('region-interest-form')) {
class RegionInterestForm extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onModalClosed = this.onModalClosed.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.endpoint = this.dataset.endpoint || '/apps/api/international-interest';
    this.shopDomain = this.dataset.shopDomain || '';

    this.panel = this.querySelector('[data-ri-panel]');
    this.successEl = this.querySelector('[data-ri-success]');
    this.successTextEl = this.querySelector('[data-ri-success-text]');
    this.emailInput = this.querySelector('[data-ri-email]');
    this.countrySelect = this.querySelector('[data-ri-country]');
    this.detectedInput = this.querySelector('[data-ri-detected]');
    this.submitBtn = this.querySelector('[data-ri-submit]');
    this.messageEl = this.querySelector('[data-ri-message]');

    this.submitText =
      this.dataset.submitText ||
      (this.submitBtn && this.submitBtn.textContent.trim()) ||
      'Notify me';
    this.loadingText = this.dataset.loadingText || 'Submitting…';
    this.msgEmailRequired = this.dataset.msgEmailRequired || 'Please enter an email address.';
    this.msgEmailInvalid = this.dataset.msgEmailInvalid || "That email address doesn't look right.";
    this.msgCountryRequired = this.dataset.msgCountryRequired || 'Please choose your country.';
    this.msgErrorGeneric = this.dataset.msgErrorGeneric || 'Something went wrong. Please try again.';
    this.msgErrorNetwork = this.dataset.msgErrorNetwork || "Couldn't reach our server. Check your connection and try again.";

    if (this.submitBtn) this.submitBtn.addEventListener('click', this.onClick);
    this.form.addEventListener('submit', this.onSubmit);
    // Reset form ↔ success swap when the surrounding modal closes so the form
    // is fresh on reopen.
    document.body.addEventListener('modalClosed', this.onModalClosed);

    // all_country_option_tags marks the shop's country (US) selected. Force the
    // placeholder so an out-of-market visitor isn't defaulted to "United States";
    // geo prefill (below) or the user then chooses. Track user edits so the
    // async geo prefill never clobbers a manual choice.
    this.userTouched = false;
    if (this.countrySelect) {
      this.countrySelect.selectedIndex = 0;
      this.countrySelect.addEventListener('change', () => { this.userTouched = true; });
    }

    this.prefillCountryFromGeo();
  }

  disconnectedCallback() {
    if (this.submitBtn) this.submitBtn.removeEventListener('click', this.onClick);
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    document.body.removeEventListener('modalClosed', this.onModalClosed);
  }

  // Best-effort: set the detected-code hidden field and preselect the matching
  // country option. Never overrides a choice the user has already made.
  prefillCountryFromGeo() {
    if (!window.EBGeo || !window.EBGeo.get) return;
    window.EBGeo.get().then((geo) => {
      const iso = geo && geo.country_code;
      if (!iso) return;
      if (this.detectedInput) this.detectedInput.value = iso;
      this.selectCountryByIso(iso);
    }).catch(() => {});
  }

  selectCountryByIso(iso) {
    if (!this.countrySelect) return;
    // Never clobber a choice the user has made since load.
    if (this.userTouched) return;

    let name = '';
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        name = new Intl.DisplayNames(['en'], { type: 'region' }).of(String(iso).toUpperCase()) || '';
      }
    } catch (e) { name = ''; }
    if (!name) return;

    const target = this.normalize(name);
    const opts = this.countrySelect.options;
    for (let i = 0; i < opts.length; i++) {
      if (this.normalize(opts[i].value) === target || this.normalize(opts[i].text) === target) {
        this.countrySelect.value = opts[i].value;
        return;
      }
    }
  }

  normalize(s) {
    return String(s).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  onModalClosed() {
    if (this.successEl && this.successEl.hidden) return; // never swapped
    if (this.panel) this.panel.hidden = false;
    if (this.successEl) this.successEl.hidden = true;
    this.clearMessage();
  }

  onClick(event) {
    event.preventDefault();
    this.submit();
  }

  onSubmit(event) {
    event.preventDefault();
    this.submit();
  }

  submit() {
    if (!this.form) return;

    const honeypot = this.form.querySelector('[data-ri-hp]');
    if (honeypot && honeypot.value) {
      // Bot detected: show the normal success state (like Ops' honeypot reply)
      // so it doesn't retry, but send nothing.
      this.showSuccess('');
      return;
    }

    const email = this.emailInput ? this.emailInput.value.trim() : '';
    if (!email) {
      this.showMessage('error', this.msgEmailRequired);
      if (this.emailInput) this.emailInput.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showMessage('error', this.msgEmailInvalid);
      if (this.emailInput) this.emailInput.focus();
      return;
    }

    const country = this.countrySelect ? (this.countrySelect.value || '').trim() : '';
    if (!country) {
      this.showMessage('error', this.msgCountryRequired);
      if (this.countrySelect) this.countrySelect.focus();
      return;
    }

    const payload = {
      email,
      country,
      detected_country_code: this.detectedInput ? this.detectedInput.value : null,
      shop_domain: this.shopDomain || null,
      company: honeypot ? honeypot.value : ''
    };

    this.setLoading(true);
    this.clearMessage();

    fetch(this.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(async (response) => {
        let data = null;
        try { data = await response.json(); } catch (e) { data = null; }
        this.setLoading(false);

        if (response.ok && data && data.ok) {
          this.fireAnalytics(payload);
          this.showSuccess(country);
          // Let the region gate suppress the popup permanently for this visitor.
          document.dispatchEvent(new CustomEvent('region-interest:submitted', {
            detail: { country: country }
          }));
          return;
        }
        const msg = (data && data.error) || this.msgErrorGeneric;
        console.error('International-interest submit failed', { status: response.status, data });
        this.showMessage('error', msg);
      })
      .catch((err) => {
        console.error('International-interest network error', err);
        this.setLoading(false);
        this.showMessage('error', this.msgErrorNetwork);
      });
  }

  fireAnalytics(payload) {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'international_interest_signup', {
        country: payload.country || undefined,
        detected_country_code: payload.detected_country_code || undefined
      });
    }
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = isLoading;
    this.submitBtn.textContent = isLoading ? this.loadingText : this.submitText;
  }

  clearMessage() {
    if (!this.messageEl) return;
    this.messageEl.hidden = true;
    this.messageEl.textContent = '';
    this.messageEl.classList.remove('bis-form__message--success', 'bis-form__message--error');
  }

  showMessage(type, text) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.hidden = false;
    this.messageEl.classList.remove('bis-form__message--success', 'bis-form__message--error');
    this.messageEl.classList.add(
      type === 'success' ? 'bis-form__message--success' : 'bis-form__message--error'
    );
  }

  showSuccess(country) {
    if (this.form) this.form.reset();
    this.clearMessage();

    if (this.successTextEl && country) {
      this.successTextEl.textContent =
        'We\'ll email you the moment Element Brooklyn ships to ' + country + '.';
    }
    if (this.panel) this.panel.hidden = true;
    if (this.successEl) this.successEl.hidden = false;

    // The focused submit button was just hidden with the panel: move focus to
    // the success heading so keyboard and screen-reader users land on the
    // confirmation. No auto-close; the visitor dismisses the modal themselves.
    const heading = this.querySelector('[data-ri-success-heading]');
    if (heading) heading.focus();
  }
}

customElements.define('region-interest-form', RegionInterestForm);
}
